import classNames from 'classnames'
import type {ReactNode} from 'react'
import {memo, useCallback, useEffect, useRef} from 'react'

import {useSandbox} from '../../../../../../utils/fastdom'
import {ansiRegexpString, getSearchRegExp} from '../../../BuildLog.utils'
import RegExpMatch from '../RegExpMatch/RegExpMatch'

import styles from './LogMessageSearchHighlighting.css'

type Props = {
  searchQuery: string
  text: string
  className: string
  searchMatchPosition?: number | null
  scrollToSearch?: boolean | null
}

const renderUnmatched = (key: string, text: string) => <span key={key}>{text}</span>

const renderNoMatches = (_: string) => null

function LogMessageSearchHighlighting({
  searchQuery,
  text,
  className,
  searchMatchPosition,
  scrollToSearch,
}: Props) {
  const ref = useRef<HTMLSpanElement>(null)
  const sandbox = useSandbox()
  useEffect(() => {
    if (searchMatchPosition != null && scrollToSearch) {
      sandbox.mutate(() => ref.current?.scrollIntoView({block: 'center'}))
    }
  }, [searchMatchPosition, sandbox, scrollToSearch])
  const renderMatched = useCallback(
    (key: string, mathchedText: string, matchPosition: number) => (
      <span
        ref={searchMatchPosition === matchPosition ? ref : undefined}
        key={key}
        className={classNames(styles.hightlighting, {
          [styles.bright]: searchMatchPosition === matchPosition,
        })}
      >
        {mathchedText}
      </span>
    ),
    [searchMatchPosition],
  )
  const renderResult = useCallback(
    (result: ReactNode) => <span className={className}>{result}</span>,
    [className],
  )

  if (searchQuery == null) {
    return null
  }

  const cleanText = text.replace(new RegExp(ansiRegexpString, 'gi'), '')
  return (
    <RegExpMatch
      text={cleanText}
      regexp={getSearchRegExp(searchQuery)}
      renderMatched={renderMatched}
      renderUnmatched={renderUnmatched}
      renderNoMatches={renderNoMatches}
      renderResult={renderResult}
    />
  )
}

export default memo<Props>(LogMessageSearchHighlighting)
